import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Price — wrapper used only in the Capacitor wrapper (iOS / future Android).
 *
 * Renders any auth flow (Login, RegisterChoice, HotelRegister, DriverRegister,
 * ClientRegister) inside the same light Price universe as the marketing
 * website — same warm cream background, same teal primary, same gold accent
 * — but laid out for a native thumb-driven experience: full-bleed, no
 * marketing navbar, safe-area aware, fade-up entrance animation keyed on the
 * URL so navigating between role pages feels like a native segue.
 */
export default function NativeAuthShell({ title, subtitle, children, hideBack = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    setReady(false);
    setAnimKey((k) => k + 1);
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, [location.pathname]);

  return (
    <div
      className="relative min-h-screen overflow-hidden text-foreground"
      style={{
        background:
          "radial-gradient(120% 80% at 50% 0%, hsl(174 30% 92%) 0%, hsl(180 20% 98%) 55%, hsl(180 20% 98%) 100%)",
      }}
      data-testid="native-auth-shell"
    >
      {/* Subtle gold sheen — kept gentle so forms stay readable. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-20 -top-16 h-72 w-72 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, hsla(32, 60%, 64%, 0.30), transparent 70%)" }}
      />

      <div
        className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col"
        style={{
          paddingTop: "max(16px, env(safe-area-inset-top))",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
          paddingLeft: "max(20px, env(safe-area-inset-left))",
          paddingRight: "max(20px, env(safe-area-inset-right))",
        }}
      >
        <div className="flex items-center justify-between">
          {!hideBack ? (
            <button
              type="button"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
              className="grid h-10 w-10 -translate-x-1 place-items-center rounded-full bg-white/70 text-primary shadow-[0_2px_8px_-4px_rgba(15,38,52,0.15)] backdrop-blur-md transition-all duration-200 hover:bg-white active:scale-95"
              data-testid="native-auth-back"
              aria-label="Retour"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2} />
            </button>
          ) : (
            <span />
          )}
          <span className="font-[Outfit] text-[13px] tracking-[0.18em] text-primary/70">SAOR</span>
        </div>

        <div
          key={animKey}
          className={`mt-4 flex-1 transition-all duration-500 ease-out ${ready ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"}`}
        >
          {(title || subtitle) && (
            <header className="mt-2">
              {title && (
                <h1 className="font-[Outfit] text-[34px] font-light leading-[1.05] tracking-tight text-foreground">
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="mt-3 max-w-sm text-[14.5px] leading-relaxed text-muted-foreground">
                  {subtitle}
                </p>
              )}
            </header>
          )}
          <div className="mt-7">{children}</div>
        </div>
      </div>
    </div>
  );
}
